export const s3Config = {
  name: "S3",
  displayName: "AWS S3",
  description: "Amazon S3 bucket security monitoring",
  icon: "/s3.png",
  category: "Cloud Storage",

  // API Endpoints
  endpoints: {
    list: {
      url: "api/s3-credentials/buckets/unified",
      method: "POST",
      useFilters: true,
      filterPreset: "onboardedS3Credentials",
    },
    details: "api/s3-credentials/buckets/{id}",
    findings: {
      url: "api/s3-findings/bucket/{id}",
      method: "POST",
      useFilters: true,
    },
    stats: "api/findings/cards", // Uses general findings stats for now
    counts: "api/s3-credentials/counts/{id}",
    create: "api/s3-credentials/buckets",
    update: "api/s3-credentials/buckets/{id}",
    delete: "api/s3-credentials/buckets/{id}",
    schedule: "api/s3-credentials/schedule",
    searchOnboarded: {url:"api/s3-search/buckets", method:"GET"},
  },

  // Card Configuration
  cardFields: [
    {
      key: "name",
      label: "Bucket Name",
      type: "title",
      truncate: true,
    },
    {
      key: "status",
      label: "Status",
      type: "status",
      statusMap: {
        active: { color: "green", label: "Active" },
        scanning: { color: "orange", label: "Scanning" },
        inactive: { color: "gray", label: "Inactive" },
      },
    },
    {
      key: "totalFindings",
      label: "Total Findings",
      type: "number",
    },
  ],

  // Table Columns for Findings
  findingColumns: [
    {
      accessorKey: "verified",
      header: "Category",
      cell: "verification_status",
    },
    {
      accessorKey: "detector_type",
      header: "Source",
      cell: "text",
    },
    {
      accessorKey: "raw_result",
      header: "Raw Result",
      cell: "truncated_text",
      maxLength: 10,
    },
    {
      accessorKey: "file",
      header: "File",
      cell: "file_link",
    },
    {
      accessorKey: "insights",
      header: "Insights",
      cell: "s3_insights",
    },
  ],

  // Stats Configuration
  statsCards: [
    {
      key: "total_buckets",
      title: "Total Buckets",
      dataPath: "total_integrations.total",
      subStats: [
        {
          key: "active",
          label: "Active",
          dataPath: "total_integrations.active",
        },
        {
          key: "inactive",
          label: "Inactive",
          dataPath: "total_integrations.inactive",
        },
      ],
    },
    {
      key: "encryption_status",
      title: "Encryption Status",
      dataPath: "encryption_stats.types",
      type: "tag_list",
      displayKey: "type",
      countKey: "count",
    },
    {
      key: "scan_status",
      title: "Scan Status",
      dataPath: "scan_status.completed",
      subText: "Completed in last {hours}h",
      subStats: [
        { key: "running", label: "Running", dataPath: "scan_status.running" },
        { key: "failed", label: "Failed", dataPath: "scan_status.failed" },
      ],
    },
    {
      key: "critical_findings",
      title: "Critical Findings",
      dataPath: "critical_findings.active",
      badge: { text: "Active", color: "red" },
      subText: "of {total} Total Findings",
      subTextPath: "critical_findings.total",
      subStats: [
        {
          key: "unvalidated",
          label: "Unvalidated",
          dataPath: "critical_findings.unvalidated",
        },
        {
          key: "resolved",
          label: "Resolved",
          dataPath: "critical_findings.resolved",
        },
      ],
    },
  ],

  // Action Menu Items
  actions: [
    {
      key: "create_ticket",
      label: "Create Ticket",
      icon: "ExternalLink",
      action: "api",
      endpoint: "api/s3-jira/tickets/bulk/create",
      method: "POST",
    },
    {
      key: "ignore_findings",
      label: "Ignore Finding",
      icon: "X",
      action: "api",
      endpoint: "api/s3-jira/tickets/bulk/ignore",
      method: "PUT",
    },
    {
      key: "resolve_ticket",
      label: "Resolve Ticket",
      icon: "X",
      action: "api",
      endpoint: "api/s3-jira/tickets/bulk/resolve",
      method: "PUT",
    },
    {
      key: "move_to_vault",
      label: "Move to Vault",
      icon: "Archive",
      action: "api",
      endpoint: "api/v1/s3/remediation",
      baseUrl: "AI_URL",
      method: "POST",
    },
  ],

  // Bulk Actions Configuration
  bulkActions: [
    {
      key: "createTickets",
      label: "Create Tickets",
      action: "createTickets",
      requiresValidation: "ticketing",
      endpoint: "api/s3-jira/tickets/bulk/create",
      method: "POST",
    },
    {
      key: "ignoreFindings",
      label: "Ignore Findings",
      action: "ignoreFindings",
      endpoint: "api/s3-jira/tickets/bulk/ignore",
      method: "PUT",
    },
    {
      key: "resolveTickets",
      label: "Resolve Tickets",
      action: "resolveTickets",
      requiresValidation: "ticketing",
      endpoint: "api/s3-jira/tickets/bulk/resolve",
      method: "PUT",
    },
    {
      key: "moveToVault",
      label: "Move to Vault",
      action: "moveToVault",
      requiresValidation: "vault",
      endpoint: "api/v1/s3/remediation",
      baseUrl: "AI_URL",
      method: "POST",
    },
  ],

  // Filter Configuration
  filterConfig: {
    serverSideFiltering: true,
    supportedFilters: ["verification", "source", "status"],
    defaultFilter: "all",
    filterEndpoint: "api/s3-findings/bucket/{id}/filter-options",
    statusMapping: {
      verified: "active",
      unverified: "unvalidated",
      ignored: "ignored"
    },
    filterLabels: {
      verified: "Verified",
      unverified: "Unverified",
      ignored: "Ignored"
    }
  },

  // Validation Requirements
  validation: {
    ticketing: {
      required: ["jira_url", "freshservice_url"],
      message:
        "Please enter the ticketing credentials in the configuration page",
    },
    vault: {
      required: ["hashicorp_vault_url"],
      message: "Please enter the vault credentials in the configuration page",
    },
  },

  // Integration-specific features
  features: {
    hasInsights: true,
    hasBlastRadius: true,
    supportsAutoFix: false,
    supportsVaultRemediation: true,
    supportsTicketing: true,
  },

  // Onboarding Configuration
  onboarding: {
    enabled: true,
    route: "/integration/s3/onboarding",
    buttonText: "Scan New Bucket",
  },
};